import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ProfileState, HealthProfile } from '../../types';
import { profileService } from '../../services/profileService';

// Initial state
const initialState: ProfileState = {
  profile: null,
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchProfile = createAsyncThunk(
  'profile/fetch',
  async (_, { rejectWithValue }) => {
    try {
      return await profileService.getProfile();
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // Profile not found is OK
      }
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch profile');
    }
  }
);

export const saveProfile = createAsyncThunk(
  'profile/save',
  async (profile: HealthProfile, { rejectWithValue }) => {
    try {
      await profileService.saveProfile(profile);
      return profile;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save profile');
    }
  }
);

export const acceptTerms = createAsyncThunk(
  'profile/acceptTerms',
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await profileService.acceptTerms();
      return response.termsAcceptedAt;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to accept terms');
    }
  }
);

// Slice
const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearProfile: (state) => {
      state.profile = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch profile
      .addCase(fetchProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Save profile
      .addCase(saveProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload;
      })
      .addCase(saveProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Accept terms
      .addCase(acceptTerms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(acceptTerms.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.profile) {
          state.profile.termsAcceptedAt = action.payload;
        } else {
          state.profile = { termsAcceptedAt: action.payload };
        }
      })
      .addCase(acceptTerms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearProfile } = profileSlice.actions;
export default profileSlice.reducer;
